import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield, WarningCircle, Prohibit, MagnifyingGlass, Trash, Pill, Stethoscope, ArrowsClockwise, Clock, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import PrescribeMedicationModal from "@/components/PrescribeMedicationModal";
import FormularyReference from "@/components/FormularyReference";

const RISK_STYLES = {
  safe:    { label: "Safe",    icon: Shield,        color: "#4ADE80", bg: "rgba(74,222,128,0.12)" },
  caution: { label: "Caution", icon: WarningCircle, color: "#FBBF24", bg: "rgba(251,191,36,0.12)" },
  avoid:   { label: "Avoid",   icon: Prohibit,      color: "#F87171", bg: "rgba(248,113,113,0.12)" },
  unknown: { label: "Unknown", icon: WarningCircle, color: "#A3ACA7", bg: "rgba(163,172,167,0.12)" },
};

export default function Medications() {
  const { user, viewingPatient } = useAuth();
  const isProviderViewing = user?.role === "provider" && viewingPatient?.patient_id;
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [meds, setMeds] = useState([]);
  const [egfr, setEgfr] = useState(null);
  const [prescribeOpen, setPrescribeOpen] = useState(false);

  const load = async () => {
    const [medsRes, egfrRes] = await Promise.all([
      api.get("/meds"),
      api.get("/tracker/egfr"),
    ]);
    setMeds(medsRes.data);
    if (egfrRes.data.length) setEgfr(egfrRes.data[egfrRes.data.length - 1].egfr);
  };

  useEffect(() => { load(); }, []);

  const check = async () => {
    if (!query.trim()) return;
    const res = await api.post("/meds/check", { medication_name: query.trim(), egfr });
    setResult(res.data);
  };

  const addToRegimen = async () => {
    if (!result) return;
    await api.post("/meds", {
      name: result.medication_name,
      risk_level: result.risk_level,
      reason: result.reason,
    });
    toast.success(`${result.medication_name} added to your regimen`);
    await load();
  };

  const remove = async (id) => {
    await api.delete(`/meds/${id}`);
    await load();
  };

  const style = result ? RISK_STYLES[result.risk_level] || RISK_STYLES.unknown : null;
  const Icon = style ? style.icon : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="meds-grid">
      {/* Checker */}
      <div className="ros-card lg:col-span-2" data-testid="med-checker">
        <div className="ros-label">Nephrotoxicity checker</div>
        <h2 className="font-heading text-2xl mt-1">Medication safety score</h2>
        <p className="text-xs text-[#A3ACA7] mt-1">
          Enter a drug name. Score adjusts to your latest eGFR{egfr ? ` (${egfr.toFixed(1)})` : ""}.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 bg-[#1C201E] border border-[#272E2B] rounded-full px-4 py-2.5">
            <MagnifyingGlass size={18} className="text-[#A3ACA7]" />
            <Input
              data-testid="med-query-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. Ibuprofen, Lisinopril, Metformin…"
              className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-auto p-0 text-white placeholder:text-[#A3ACA7]"
              onKeyDown={(e) => e.key === "Enter" && check()}
            />
          </div>
          <Button onClick={check} data-testid="med-check-btn"
                  className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold">
            Check
          </Button>
        </div>

        {result && style && (
          <div className="mt-6 ros-card-raised" data-testid="med-result-card"
               style={{ borderColor: `${style.color}44` }}>
            <div className="flex items-start gap-4">
              <div className="rounded-2xl p-3" style={{ background: style.bg }}>
                <Icon size={28} weight="duotone" color={style.color} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="font-heading text-xl">{result.medication_name}</div>
                  <span className="text-xs px-3 py-1 rounded-full font-semibold"
                        style={{ color: style.color, background: style.bg }}>
                    {style.label.toUpperCase()}
                  </span>
                </div>
                <div className="text-xs text-[#A3ACA7] mt-1">{result.category}</div>
                <p className="text-sm text-white/90 mt-3">{result.reason}</p>
                {result.alternatives && result.alternatives.length > 0 && (
                  <div className="mt-4">
                    <div className="ros-label">Alternatives</div>
                    <ul className="mt-2 space-y-1 text-sm">
                      {result.alternatives.map((a) => (
                        <li key={a} className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-[#4ADE80]" /> {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <Button onClick={addToRegimen} data-testid="med-add-btn" variant="outline"
                        className="rounded-full mt-4 border-[#272E2B] hover:bg-[#1C201E]">
                  Add to my regimen
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-[11px] text-[#A3ACA7]">
          This tool is for education only. Always discuss changes with your nephrologist.
        </div>
      </div>

      {/* Current regimen */}
      <div className="ros-card" data-testid="regimen-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="ros-label">Current regimen</div>
            <div className="text-xs text-[#A3ACA7] mt-1">{meds.length} medications</div>
          </div>
          {isProviderViewing && (
            <button
              onClick={() => setPrescribeOpen(true)}
              data-testid="prescribe-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#60A5FA] text-black hover:bg-[#60A5FA]/90"
            >
              <Pill size={12} weight="bold" /> Prescribe
            </button>
          )}
        </div>
        <div className="mt-4 space-y-3">
          {meds.length === 0 && (
            <div className="text-sm text-[#A3ACA7] py-6 text-center">
              No medications tracked yet.
            </div>
          )}
          {meds.map((m) => {
            const s = RISK_STYLES[m.risk_level] || RISK_STYLES.unknown;
            const I = s.icon;
            const isPrescribed = m.source === "prescribed_by_provider";
            return (
              <div key={m.id} className="ros-card-raised" data-testid={`med-item-${m.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <I size={16} weight="duotone" color={s.color} />
                      <div className="font-medium">{m.name}</div>
                      {isPrescribed && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#60A5FA] bg-[#60A5FA]/10 border border-[#60A5FA]/30 px-2 py-0.5 rounded-full">
                          <Stethoscope size={10} weight="bold" /> Prescribed
                        </span>
                      )}
                    </div>
                    {m.dose && <div className="text-[11px] text-[#A3ACA7] mt-1">{m.dose}{m.frequency ? ` · ${m.frequency}` : ""}</div>}
                    {isPrescribed && (
                      <div className="text-[10px] text-[#A3ACA7] mt-1">
                        by <span className="text-white/80 font-semibold">{m.prescribed_by_name}</span>
                        {m.prescribed_at && <> · {m.prescribed_at.slice(0, 10)}</>}
                        {m.indication && <> · {m.indication}</>}
                      </div>
                    )}
                    {/* Refill status chip + action */}
                    {isPrescribed && m.refill_status && (() => {
                      const rs = m.refill_status;
                      const style = rs.status === "overdue"
                        ? { color: "#F87171", bg: "#F87171/12", brd: "#F87171/40", label: `Refill overdue by ${Math.abs(rs.days_remaining)}d` }
                        : rs.status === "due_soon"
                        ? { color: "#FBBF24", bg: "#FBBF24/12", brd: "#FBBF24/40", label: `Refill due in ${rs.days_remaining}d` }
                        : { color: "#4ADE80", bg: "#4ADE80/8", brd: "#4ADE80/30", label: `${rs.days_remaining}d supply left` };
                      return (
                        <div className="flex items-center gap-2 mt-2 flex-wrap"
                             data-testid={`refill-status-${m.id}-${rs.status}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border"
                                style={{ color: style.color, background: `${style.color}18`, borderColor: `${style.color}55` }}>
                            <Clock size={10} weight="bold" /> {style.label}
                          </span>
                          <span className="text-[10px] text-[#A3ACA7]">
                            Expected: {rs.expected_refill_date} · {rs.supply_days}-day supply
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                await api.post(`/meds/${m.id}/refill`);
                                toast.success("Refill recorded — countdown reset");
                                load();
                              } catch { toast.error("Could not record refill"); }
                            }}
                            data-testid={`refill-btn-${m.id}`}
                            className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-[#4ADE80] border border-[#4ADE80]/40 hover:bg-[#4ADE80] hover:text-black transition-colors px-2 py-0.5 rounded-full"
                          >
                            <ArrowsClockwise size={10} weight="bold" /> I refilled
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  <button onClick={() => remove(m.id)} className="text-[#A3ACA7] hover:text-[#F87171] p-1"
                          data-testid={`delete-med-${m.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
                {m.reason && <p className="text-xs text-[#A3ACA7] mt-2">{m.reason}</p>}
              </div>
            );
          })}
        </div>
      </div>

      <PrescribeMedicationModal
        open={prescribeOpen}
        onClose={() => setPrescribeOpen(false)}
        patientId={viewingPatient?.patient_id}
        patientName={viewingPatient?.name}
        onPrescribed={() => load()}
      />

      {/* Phase 2 — Per-specialty formulary reference */}
      <FormularyReference defaultSpecialty={user?.specialty || "nephrology"} />
    </div>
  );
}
